import type { BookRecord, PlatformRole } from "./types.js";

export type ActiveView =
  | "agent"
  | "launch"
  | "today"
  | "research"
  | "case_lab"
  | "books"
  | "create_book"
  | "workspace"
  | "positions"
  | "add_holdings"
  | "position_detail"
  | "assumptions"
  | "assumption_detail"
  | "scenarios"
  | "coverage"
  | "scenario_detail"
  | "scenario_diff"
  | "new_scenario"
  | "evidence"
  | "evidence_new"
  | "evidence_detail"
  | "episode_detail"
  | "thesis"
  | "governance"
  | "investigations"
  | "investigation_detail"
  | "decision_packs"
  | "report_detail"
  | "runs"
  | "run_detail"
  | "whatif"
  | "trading"
  | "trading_goal"
  | "calibration"
  | "research_notes_all"
  | "reports_all"
  | "analysis_all"
  | "reviews_all"
  | "data"
  /** V-M6: the old data explorer, moved under More. */
  | "advanced_data"
  | "saved_results"
  | "connect"
  | "imports"
  | "import_detail"
  | "account"
  | "admin";
export type RouteIssue = "not_found" | "book_not_found" | "permission_denied";

export interface RouteResolution {
  activeView: ActiveView;
  bookId?: string;
  entityId?: string;
  fromVersionId?: string;
  toVersionId?: string;
  issue?: RouteIssue;
  canonicalPath: string;
  shouldReplace: boolean;
}

interface RequestedRoute {
  activeView: ActiveView;
  bookId?: string;
  entityId?: string;
  fromVersionId?: string;
  toVersionId?: string;
  issue?: RouteIssue;
  legacy: boolean;
}

export function resolveAppRoute(
  url: URL,
  books: Pick<BookRecord, "id">[],
  role: PlatformRole,
): RouteResolution {
  const requested = requestedRoute(url);
  if (requested.issue !== undefined) {
    return {
      activeView: "today",
      issue: requested.issue,
      canonicalPath: `${url.pathname}${url.search}`,
      shouldReplace: false,
    };
  }

  if (
    (requested.activeView === "admin" || requested.activeView === "create_book") &&
    role !== "admin"
  ) {
    return {
      activeView: "today",
      issue: "permission_denied",
      canonicalPath: `${url.pathname}${url.search}`,
      shouldReplace: false,
    };
  }

  let bookId = requested.bookId;
  if (isBookRoute(requested.activeView)) {
    bookId ??= books.at(0)?.id;
    if (bookId !== undefined && !books.some((book) => book.id === bookId)) {
      return {
        activeView: "today",
        issue: "book_not_found",
        canonicalPath: `${url.pathname}${url.search}`,
        shouldReplace: false,
      };
    }
  }

  const canonicalPath = appRoutePath(
    requested.activeView,
    bookId,
    requested.entityId,
    requested.fromVersionId,
    requested.toVersionId,
  );
  return {
    activeView: requested.activeView,
    ...(bookId === undefined ? {} : { bookId }),
    ...(requested.entityId === undefined ? {} : { entityId: requested.entityId }),
    ...(requested.fromVersionId === undefined ? {} : { fromVersionId: requested.fromVersionId }),
    ...(requested.toVersionId === undefined ? {} : { toVersionId: requested.toVersionId }),
    canonicalPath,
    shouldReplace: requested.legacy || `${url.pathname}${url.search}` !== canonicalPath,
  };
}

export function appRoutePath(
  view: ActiveView,
  bookId?: string,
  entityId?: string,
  fromVersionId?: string,
  toVersionId?: string,
): string {
  const bookPath =
    bookId === undefined ? "/portfolios" : `/portfolios/${encodeURIComponent(bookId)}`;
  switch (view) {
    case "agent":
      return "/agent";
    case "launch":
      return "/get-started";
    case "today":
      return "/dashboard";
    case "research":
      return "/case-study";
    case "case_lab":
      return "/case-study/replay";
    case "books":
      return "/portfolios";
    case "create_book":
      return "/portfolios/new";
    case "workspace":
      return bookPath;
    case "positions":
      return `${bookPath}/positions`;
    case "add_holdings":
      return `${bookPath}/positions/add`;
    case "position_detail":
      return entityId === undefined
        ? `${bookPath}/positions`
        : `${bookPath}/positions/${encodeURIComponent(entityId)}`;
    case "assumptions":
      return `${bookPath}/assumptions`;
    case "assumption_detail":
      return entityId === undefined
        ? `${bookPath}/assumptions`
        : `${bookPath}/assumptions/${encodeURIComponent(entityId)}`;
    case "scenarios":
      return "/what-ifs";
    case "coverage":
      return "/what-ifs/coverage";
    case "scenario_detail":
      return entityId === undefined ? "/what-ifs" : `/what-ifs/${encodeURIComponent(entityId)}`;
    case "scenario_diff":
      return entityId === undefined || fromVersionId === undefined || toVersionId === undefined
        ? entityId === undefined
          ? "/what-ifs"
          : `/what-ifs/${encodeURIComponent(entityId)}`
        : `/what-ifs/${encodeURIComponent(entityId)}/versions/${encodeURIComponent(fromVersionId)}...${encodeURIComponent(toVersionId)}`;
    case "new_scenario":
      return "/what-ifs/new";
    case "evidence":
      return `${bookPath}/evidence`;
    case "evidence_new":
      return `${bookPath}/evidence/new`;
    case "evidence_detail":
      return entityId === undefined
        ? `${bookPath}/evidence`
        : `${bookPath}/evidence/${encodeURIComponent(entityId)}`;
    case "episode_detail":
      return entityId === undefined
        ? `${bookPath}/evidence`
        : `${bookPath}/episodes/${encodeURIComponent(entityId)}`;
    case "thesis":
      return `${bookPath}/assumptions/extract`;
    case "governance":
      return `${bookPath}/governance`;
    case "investigations":
      return `${bookPath}/research`;
    case "investigation_detail":
      return entityId === undefined
        ? `${bookPath}/research`
        : `/research/${encodeURIComponent(entityId)}`;
    case "decision_packs":
      return `${bookPath}/reports`;
    case "report_detail":
      return entityId === undefined ? "/reports" : `/reports/${encodeURIComponent(entityId)}`;
    case "runs":
      return `${bookPath}/runs`;
    case "run_detail":
      return entityId === undefined ? "/what-ifs" : `/runs/${encodeURIComponent(entityId)}`;
    case "whatif":
      return `${bookPath}/quick-test`;
    case "trading":
      return "/trader";
    case "calibration":
      return "/calibration";
    case "research_notes_all":
      return "/research-notes";
    case "reports_all":
      return "/reports";
    case "analysis_all":
      return "/analysis-activity";
    case "reviews_all":
      return "/reviews";
    case "trading_goal":
      return entityId === undefined ? "/trader" : `/trader/goals/${encodeURIComponent(entityId)}`;
    case "data":
      return "/data";
    case "advanced_data":
      return "/data/advanced";
    case "saved_results":
      return "/data/saved";
    case "connect":
      return "/data/sources/add";
    case "imports":
      return `${bookPath}/data`;
    case "import_detail":
      return entityId === undefined
        ? "/data/sources/add"
        : `/data/imports/${encodeURIComponent(entityId)}`;
    case "account":
      return "/account";
    case "admin":
      return "/administration";
  }
}

function requestedRoute(url: URL): RequestedRoute {
  if (url.pathname === "/") return legacyOrLanding(url);
  if (url.pathname === "/sign-up") return { activeView: "today", legacy: true };
  if (url.pathname === "/onboarding") return { activeView: "launch", legacy: true };
  if (url.pathname === "/agent") return { activeView: "agent", legacy: false };
  if (url.pathname === "/get-started") return { activeView: "launch", legacy: false };
  if (url.pathname === "/dashboard") return { activeView: "today", legacy: false };
  if (url.pathname === "/today") return { activeView: "today", legacy: true };
  if (url.pathname === "/case-study") return { activeView: "research", legacy: false };
  if (url.pathname === "/case-study/replay") {
    return { activeView: "case_lab", legacy: false };
  }
  if (url.pathname === "/research") return { activeView: "research", legacy: true };
  if (url.pathname === "/research/situational-awareness-second-order/lab") {
    return { activeView: "case_lab", legacy: true };
  }
  if (url.pathname === "/portfolios") return { activeView: "books", legacy: false };
  if (url.pathname === "/portfolios/new") return { activeView: "create_book", legacy: false };
  if (url.pathname === "/books") return { activeView: "books", legacy: true };
  if (url.pathname === "/books/new") return { activeView: "create_book", legacy: true };
  if (url.pathname === "/what-ifs") return { activeView: "scenarios", legacy: false };
  if (url.pathname === "/what-ifs/new") return { activeView: "new_scenario", legacy: false };
  if (url.pathname === "/what-ifs/coverage") {
    return { activeView: "coverage", legacy: false };
  }
  if (url.pathname === "/trader") return { activeView: "trading", legacy: false };
  if (url.pathname === "/calibration") return { activeView: "calibration", legacy: false };
  // V-S1d: the pages under More list every portfolio's items at a path with no portfolio in it.
  if (url.pathname === "/research-notes") {
    return { activeView: "research_notes_all", legacy: false };
  }
  if (url.pathname === "/reports") return { activeView: "reports_all", legacy: false };
  if (url.pathname === "/analysis-activity") return { activeView: "analysis_all", legacy: false };
  if (url.pathname === "/reviews") return { activeView: "reviews_all", legacy: false };
  if (url.pathname === "/data") return { activeView: "data", legacy: false };
  if (url.pathname === "/data/advanced") return { activeView: "advanced_data", legacy: false };
  if (url.pathname === "/data/saved") return { activeView: "saved_results", legacy: false };
  if (url.pathname === "/data/sources/add") return { activeView: "connect", legacy: false };
  if (url.pathname === "/account") return { activeView: "account", legacy: false };
  if (url.pathname === "/administration") return { activeView: "admin", legacy: false };
  // ADR-100: Alpaca's redirect. main.tsx normally takes it first; this keeps it on the admin page.
  if (url.pathname === "/administration/paper-accounts/alpaca") {
    return { activeView: "admin", legacy: true };
  }
  if (url.pathname === "/governance") return { activeView: "governance", legacy: true };

  const segments = url.pathname.split("/").filter(Boolean);
  if (segments[0] === "what-ifs" && segments[2] === "versions" && segments.length === 4) {
    const entityId = safeDecode(segments[1]);
    const comparison = segments[3]?.split("...") ?? [];
    const fromVersionId = comparison.length === 2 ? safeDecode(comparison[0]) : undefined;
    const toVersionId = comparison.length === 2 ? safeDecode(comparison[1]) : undefined;
    return entityId === undefined || fromVersionId === undefined || toVersionId === undefined
      ? { activeView: "today", issue: "not_found", legacy: false }
      : {
          activeView: "scenario_diff",
          entityId,
          fromVersionId,
          toVersionId,
          legacy: false,
        };
  }
  if (segments[0] === "what-ifs" && segments.length === 2) {
    const entityId = safeDecode(segments[1]);
    return entityId === undefined
      ? { activeView: "today", issue: "not_found", legacy: false }
      : { activeView: "scenario_detail", entityId, legacy: false };
  }
  if (segments[0] === "research" && segments.length === 2) {
    const entityId = safeDecode(segments[1]);
    return entityId === undefined
      ? { activeView: "today", issue: "not_found", legacy: false }
      : { activeView: "investigation_detail", entityId, legacy: false };
  }
  if (segments[0] === "runs" && segments.length === 2) {
    const entityId = safeDecode(segments[1]);
    return entityId === undefined
      ? { activeView: "today", issue: "not_found", legacy: false }
      : { activeView: "run_detail", entityId, legacy: false };
  }
  if (segments[0] === "reports" && segments.length === 2) {
    const entityId = safeDecode(segments[1]);
    return entityId === undefined
      ? { activeView: "today", issue: "not_found", legacy: false }
      : { activeView: "report_detail", entityId, legacy: false };
  }
  if (segments[0] === "trader" && segments[1] === "goals" && segments.length === 3) {
    const entityId = safeDecode(segments[2]);
    return entityId === undefined
      ? { activeView: "today", issue: "not_found", legacy: false }
      : { activeView: "trading_goal", entityId, legacy: false };
  }
  if (segments[0] === "data" && segments[1] === "imports" && segments.length === 3) {
    const entityId = safeDecode(segments[2]);
    return entityId === undefined
      ? { activeView: "today", issue: "not_found", legacy: false }
      : { activeView: "import_detail", entityId, legacy: false };
  }
  if ((segments[0] !== "portfolios" && segments[0] !== "books") || segments.length < 2) {
    return { activeView: "today", issue: "not_found", legacy: false };
  }

  const legacyBookPath = segments[0] === "books";

  const bookId = safeDecode(segments[1]);
  if (bookId === undefined) {
    return { activeView: "today", issue: "not_found", legacy: false };
  }
  if (segments.length === 2) {
    return { activeView: "workspace", bookId, legacy: legacyBookPath };
  }
  if (segments.length === 3 && segments[2] === "overview") {
    return { activeView: "workspace", bookId, legacy: true };
  }
  if (segments.length === 3 && segments[2] === "data") {
    return { activeView: "imports", bookId, legacy: legacyBookPath };
  }
  if (segments.length === 3 && segments[2] === "connect") {
    return { activeView: "connect", bookId, legacy: true };
  }
  if (segments.length === 3 && segments[2] === "positions") {
    return { activeView: "positions", bookId, legacy: legacyBookPath };
  }
  if (segments.length === 4 && segments[2] === "positions" && segments[3] === "add") {
    return { activeView: "add_holdings", bookId, legacy: legacyBookPath };
  }
  if (segments.length === 4 && segments[2] === "positions") {
    const entityId = safeDecode(segments[3]);
    return entityId === undefined
      ? { activeView: "today", issue: "not_found", legacy: false }
      : { activeView: "position_detail", bookId, entityId, legacy: legacyBookPath };
  }
  if (segments.length === 3 && segments[2] === "assumptions") {
    return { activeView: "assumptions", bookId, legacy: legacyBookPath };
  }
  if (segments.length === 4 && segments[2] === "assumptions" && segments[3] === "extract") {
    return { activeView: "thesis", bookId, legacy: legacyBookPath };
  }
  if (segments.length === 4 && segments[2] === "assumptions") {
    const entityId = safeDecode(segments[3]);
    return entityId === undefined
      ? { activeView: "today", issue: "not_found", legacy: false }
      : { activeView: "assumption_detail", bookId, entityId, legacy: legacyBookPath };
  }
  if (segments.length === 3 && segments[2] === "scenarios") {
    return { activeView: "scenarios", bookId, legacy: true };
  }
  if (segments.length === 4 && segments[2] === "scenarios") {
    const entityId = safeDecode(segments[3]);
    return entityId === undefined
      ? { activeView: "today", issue: "not_found", legacy: false }
      : { activeView: "scenario_detail", bookId, entityId, legacy: true };
  }
  if (segments.length === 3 && segments[2] === "evidence") {
    return { activeView: "evidence", bookId, legacy: legacyBookPath };
  }
  if (segments.length === 4 && segments[2] === "evidence" && segments[3] === "new") {
    return { activeView: "evidence_new", bookId, legacy: legacyBookPath };
  }
  if (segments.length === 4 && segments[2] === "evidence") {
    const entityId = safeDecode(segments[3]);
    return entityId === undefined
      ? { activeView: "today", issue: "not_found", legacy: false }
      : { activeView: "evidence_detail", bookId, entityId, legacy: legacyBookPath };
  }
  if (segments.length === 4 && segments[2] === "episodes") {
    const entityId = safeDecode(segments[3]);
    return entityId === undefined
      ? { activeView: "today", issue: "not_found", legacy: false }
      : { activeView: "episode_detail", bookId, entityId, legacy: legacyBookPath };
  }
  if (segments.length === 3 && segments[2] === "thesis-extraction") {
    return { activeView: "thesis", bookId, legacy: true };
  }
  if (segments.length === 3 && segments[2] === "governance") {
    return { activeView: "governance", bookId, legacy: legacyBookPath };
  }
  if (segments.length === 3 && segments[2] === "investigations") {
    return { activeView: "investigations", bookId, legacy: true };
  }
  if (segments.length === 4 && segments[2] === "investigations") {
    const entityId = safeDecode(segments[3]);
    return entityId === undefined
      ? { activeView: "today", issue: "not_found", legacy: false }
      : { activeView: "investigation_detail", bookId, entityId, legacy: true };
  }
  if (segments.length === 3 && segments[2] === "decision-packs") {
    return { activeView: "decision_packs", bookId, legacy: true };
  }
  if (segments.length === 3 && segments[2] === "research") {
    return { activeView: "investigations", bookId, legacy: legacyBookPath };
  }
  if (segments.length === 3 && segments[2] === "reports") {
    return { activeView: "decision_packs", bookId, legacy: legacyBookPath };
  }
  if (segments.length === 3 && segments[2] === "runs") {
    return { activeView: "runs", bookId, legacy: legacyBookPath };
  }
  if (segments.length === 3 && segments[2] === "what-if") {
    return { activeView: "whatif", bookId, legacy: true };
  }
  if (segments.length === 3 && segments[2] === "quick-test") {
    return { activeView: "whatif", bookId, legacy: legacyBookPath };
  }
  if (segments.length === 3 && segments[2] === "goal") {
    // A portfolio's goal page before v3; the Trader lists every portfolio's goal now.
    return { activeView: "trading", legacy: true };
  }
  return { activeView: "today", issue: "not_found", legacy: false };
}

export function isBookRoute(view: ActiveView): boolean {
  return [
    "workspace",
    "positions",
    "add_holdings",
    "position_detail",
    "assumptions",
    "assumption_detail",
    "evidence",
    "evidence_new",
    "evidence_detail",
    "episode_detail",
    "thesis",
    "governance",
    "investigations",
    "investigation_detail",
    "decision_packs",
    "runs",
    "whatif",
    "connect",
    "imports",
  ].includes(view);
}

function legacyOrLanding(url: URL): RequestedRoute {
  const view = url.searchParams.get("view");
  const bookId = url.searchParams.get("book") ?? undefined;
  if (view === "admin") return { activeView: "admin", legacy: true };
  if (view === "imports") {
    return { activeView: "imports", ...(bookId === undefined ? {} : { bookId }), legacy: true };
  }
  if (view === "workspace" || bookId !== undefined) {
    return { activeView: "workspace", ...(bookId === undefined ? {} : { bookId }), legacy: true };
  }
  if (view !== null) return { activeView: "today", legacy: true };
  // v3 opens the app on Ask Polyfuture.
  return { activeView: "agent", legacy: true };
}

function safeDecode(value: string | undefined): string | undefined {
  if (value === undefined || value.length === 0) return undefined;
  try {
    return decodeURIComponent(value);
  } catch {
    return undefined;
  }
}
